import { isInstanceOf, } from "./aux";
import { add, angle, arcSegmentContent, boundingBox, centerOfArc, centerOfPath, closePoints, dTol, dimensions, endTangent, isCircle, isClosed, lineSegmentContent, mult, normalize, radius, reverseArcSegment, reverseLineSegment, rotate, rotateCcw, rotateCw, squareNorm, toDegree, translate, translateLineSegment, vector, squareDistance, } from "./geometry";
import { SegmentType, } from "./wsi4_interfaces";
import { isArcSegment, isNumber, } from "./wsi4_typeguards";
export var PartType;
(function (PartType) {
    PartType["circleCutout"] = "circleCutout";
    PartType["rectangleCutout"] = "rectangleCutout";
    PartType["sheetMetalProfile"] = "sheetMetalProfile";
    PartType["userDefinedTube"] = "userDefinedTube";
})(PartType || (PartType = {}));
function computeLowerPath(part, lastPart, thickness) {
    const r = part.content.outerAngleInRadians > 0. ? 2. * thickness : thickness;
    const lastPath = lastPart === undefined ? undefined : lastPart.lower;
    const lineFrom = lastPath === undefined
        ? {
            entries: [
                0.,
                0.,
            ],
        }
        : (lastPath.arcSegment === undefined
            ? {
                entries: [
                    0.,
                    0.,
                ],
            }
            : lastPath.arcSegment.to);
    const vec = lastPath === undefined
        ? {
            entries: [
                1.,
                0.,
            ],
        }
        : (lastPath.arcSegment === undefined
            ? {
                entries: [
                    1.,
                    0.,
                ],
            }
            : endTangent(lastPath.arcSegment));
    const lineTo = add(mult(part.flangeLength.lineLength, vec), lineFrom);
    const line = lineSegmentContent(lineFrom, lineTo);
    if (part.content.outerAngleInRadians === 0.) {
        return {
            lineSegment: line,
            arcSegment: undefined,
        };
    }
    const arcCenter = centerOfArc(lineTo, vec, part.content.outerAngleInRadians > 0., r);
    const arcTo = rotate(lineTo, arcCenter, part.content.outerAngleInRadians);
    const arc = arcSegmentContent(lineTo, arcTo, arcCenter, part.content.outerAngleInRadians > 0.);
    if (Math.abs(radius(arc) - r) > dTol) {
        throw Error("Error constructing.  Radius of arc wrong: " + radius(arc)
            .toString() + " instead of " + r.toString());
    }
    if (Math.abs(angle(arc) - part.content.outerAngleInRadians) > dTol) {
        throw Error("Error constructing.  Angle of arc wrong: " + angle(arc)
            .toString() + " instead of " + part.content.outerAngleInRadians.toString());
    }
    return {
        lineSegment: line,
        arcSegment: arc,
    };
}
function computeUpperPath(lowerPath, thickness) {
    const vec = vector(lowerPath.lineSegment.from, lowerPath.lineSegment.to);
    const lineNormal = normalize(rotateCcw(vec));
    const moveLine = (p) => add(mult(thickness, lineNormal), p);
    const line = {
        from: moveLine(lowerPath.lineSegment.from),
        to: moveLine(lowerPath.lineSegment.to),
    };
    if (lowerPath.arcSegment === undefined) {
        return {
            lineSegment: line,
            arcSegment: undefined,
        };
    }
    const arcNormal = rotateCcw(endTangent(lowerPath.arcSegment));
    const arc = {
        from: line.to,
        to: add(mult(thickness, arcNormal), lowerPath.arcSegment.to),
        center: lowerPath.arcSegment.center,
        ccw: lowerPath.arcSegment.ccw,
    };
    const expectedRadius = Math.abs(radius(lowerPath.arcSegment) - thickness) < dTol ? 2. * thickness : thickness;
    if (Math.abs(radius(arc) - expectedRadius) > dTol) {
        throw Error("Error constructing.  Radius of arc wrong: " + radius(arc)
            .toString() + " instead of " + expectedRadius.toString());
    }
    const lowerAngle = angle(lowerPath.arcSegment);
    if (Math.abs(angle(arc) - lowerAngle) > dTol) {
        throw Error("Error constructing.  Angle of arc wrong: " + angle(arc)
            .toString() + " instead of " + lowerAngle.toString());
    }
    return {
        lineSegment: line,
        arcSegment: arc,
    };
}
function computeSegments(part, lastPart, thickness) {
    const lower = computeLowerPath(part, lastPart, thickness);
    return {
        lower: lower,
        upper: computeUpperPath(lower, thickness),
        angle: part.content.outerAngleInRadians,
    };
}
export function createSheetMetalProfile(parts, thickness) {
    if (parts.length < 2) {
        throw Error("Missing parts");
    }
    if (parts[parts.length - 1].content.outerAngleInRadians !== 0.) {
        throw Error("Angle of last part has to be 0.");
    }
    const paths = [];
    for (const part of parts) {
        paths.push(computeSegments(part, paths.length === 0 ? undefined : paths[paths.length - 1], thickness));
    }
    const path = [];
    for (const p of paths) {
        if (path.length !== 0) {
            if (!closePoints(path[path.length - 1].content.to, p.lower.lineSegment.from)) {
                throw Error("Error constructing.");
            }
        }
        path.push({ type: SegmentType.line, content: p.lower.lineSegment });
        if (p.lower.arcSegment !== undefined) {
            if (!closePoints(path[path.length - 1].content.to, p.lower.arcSegment.from)) {
                throw Error("Error constructing.");
            }
            path.push({ type: SegmentType.arc, content: p.lower.arcSegment });
        }
    }
    path.push({
        type: SegmentType.line,
        content: {
            from: path[path.length - 1].content.to,
            to: paths[paths.length - 1].upper.lineSegment.to,
        },
    });
    for (let i = paths.length - 1; i >= 0; --i) {
        const p = paths[i];
        if (p.upper.arcSegment !== undefined) {
            if (!closePoints(path[path.length - 1].content.to, p.upper.arcSegment.to)) {
                throw Error("Error constructing.");
            }
            path.push({ type: SegmentType.arc, content: reverseArcSegment(p.upper.arcSegment) });
        }
        path.push({ type: SegmentType.line, content: reverseLineSegment(p.upper.lineSegment) });
    }
    path.push({
        type: SegmentType.line,
        content: {
            from: path[path.length - 1].content.to,
            to: path[0].content.from,
        },
    });
    if (!isClosed(path)) {
        throw Error("Path not closed.");
    }
    return path;
}
function linePathElement(o) {
    return "L " + o.to.entries[0].toString() + "," + o.to.entries[1].toString() + " ";
}
function arcPathElementNonFullCircle(s) {
    const r = radius(s);
    const angleText = Math.abs(angle(s)) <= Math.PI ? "1" : "0";
    const o = s.ccw ? "0" : "1";
    return "A " + r.toString() + " " + r.toString() + " 0 " + angleText + " " + o + " " + s.to.entries[0].toString() + "," + s.to.entries[1].toString();
}
function arcPathElementFullCircle(s) {
    const midWay = add(s.to, mult(2., vector(s.to, s.center)));
    let str = "";
    str += arcPathElementNonFullCircle({
        from: s.from,
        to: midWay,
        center: s.center,
        ccw: s.ccw,
    });
    str += " ";
    str += arcPathElementNonFullCircle({
        from: midWay,
        to: s.to,
        center: s.center,
        ccw: s.ccw,
    });
    return str;
}
function areClose(f, s) {
    return Math.abs(f.entries[0] - s.entries[0]) < 0.001 && Math.abs(f.entries[1] - s.entries[1]) < 0.001;
}
function isFullCircle(s) {
    return areClose(s.from, s.to);
}
function createPathElement(s) {
    if (s.type === SegmentType.line) {
        return linePathElement(s.content);
    }
    else {
        const arc = s.content;
        if (isFullCircle(arc)) {
            return arcPathElementFullCircle(arc);
        }
        return arcPathElementNonFullCircle(arc);
    }
}
function createSubPath(p) {
    return "M " + p[0].content.from.entries[0].toString() + "," + p[0].content.from.entries[1].toString() + " ";
}
function createInnerOuterPolygon(outer, inner) {
    return {
        outerPolygon: outer,
        innerPolygons: inner,
    };
}
function createPathSvgString(iop) {
    let str = "";
    const addPath = (p) => {
        str += createSubPath(p);
        for (const s of p) {
            str += createPathElement(s) + " ";
        }
    };
    addPath(iop.outerPolygon);
    iop.innerPolygons.forEach(i => addPath(i));
    return str;
}
const dashedMeasurementColor = "grey";
const measurementColor = "green";
const holeLabelColor = "black";
function approximateTextLength(textContent, fontSize) {
    return textContent.length * 0.4 * fontSize;
}
function approximateTextHeight(fontSize, ccw) {
    return ccw ? 0.5 * fontSize : 1.5 * fontSize;
}
function createMeasurementElement(p, m, fill, dashed) {
    const path = document.createElementNS("http://www.w3.org/2000/svg", "path");
    path.setAttribute("d", createPathSvgString(createInnerOuterPolygon(p, [])));
    path.setAttribute("stroke", dashed ? dashedMeasurementColor : measurementColor);
    path.setAttribute("fill", fill ? measurementColor : "none");
    path.setAttribute("stroke-width", m.strokeWidth.toString());
    if (dashed) {
        path.setAttribute("stroke-dasharray", "1,1");
    }
    return path;
}
function createTrianglePath(point0, point1, point2) {
    return [
        {
            type: SegmentType.line,
            content: {
                from: point0,
                to: point1,
            },
        },
        {
            type: SegmentType.line,
            content: {
                from: point1,
                to: point2,
            },
        },
        {
            type: SegmentType.line,
            content: {
                from: point2,
                to: point0,
            },
        },
    ];
}
function textElement(origin, textContent, m) {
    const text = document.createElementNS("http://www.w3.org/2000/svg", "text");
    text.setAttribute("x", (origin.entries[0]).toString());
    text.setAttribute("y", (origin.entries[1]).toString());
    text.setAttribute("text-anchor", "middle");
    text.setAttribute("fill", measurementColor);
    text.setAttribute("stroke", measurementColor);
    text.setAttribute("stroke-width", m.strokeWidth.toString());
    text.setAttribute("font-size", m.fontSize.toFixed(0));
    text.setAttribute("font-family", "sans-serif");
    text.setAttribute("letter-spacing", "0");
    if (text.textContent !== null) {
        text.textContent = textContent;
    }
    return text;
}
function measurement(lineToMeasure, ccw, textContent, m) {
    const elements = [];
    const vec = normalize(vector(lineToMeasure.from, lineToMeasure.to));
    const normal = ccw ? rotateCw(vec) : rotateCcw(vec);
    const translated = translateLineSegment(mult(m.gap, normal), lineToMeasure);
    const linePath = [
        {
            type: SegmentType.line,
            content: {
                from: add(translated.from, mult(m.triangleWidth, vec)),
                to: add(translated.to, mult(-m.triangleWidth, vec)),
            },
        },
    ];
    const createElement = (p, fill, dashed) => { elements.push(createMeasurementElement(p, m, fill, dashed)); };
    createElement(linePath, false, false);
    {
        const helpLineLength = -squareDistance(lineToMeasure.from, lineToMeasure.to);
        createElement([
            {
                type: SegmentType.line,
                content: {
                    from: translated.from,
                    to: add(translated.from, mult(helpLineLength, normal)),
                },
            },
        ], false, true);
        createElement([
            {
                type: SegmentType.line,
                content: {
                    from: translated.to,
                    to: add(translated.to, mult(helpLineLength, normal)),
                },
            },
        ], false, true);
    }
    {
        const point0 = add(linePath[0].content.from, mult(0.5 * m.triangleWidth, normal));
        const point1 = translated.from;
        const point2 = add(linePath[0].content.from, mult(-0.5 * m.triangleWidth, normal));
        createElement(createTrianglePath(point0, point1, point2), true, false);
    }
    {
        const point0 = add(linePath[0].content.to, mult(0.5 * m.triangleWidth, normal));
        const point1 = translated.to;
        const point2 = add(linePath[0].content.to, mult(-0.5 * m.triangleWidth, normal));
        createElement(createTrianglePath(point0, point1, point2), true, false);
    }
    {
        const n = {
            entries: [
                approximateTextLength(textContent, m.fontSize) * normal.entries[0],
                approximateTextHeight(m.fontSize, ccw) * normal.entries[1],
            ],
        };
        const origin = add(centerOfPath(linePath), n);
        elements.push(textElement(origin, textContent, m));
    }
    return elements;
}
function computeMeasurementStyle(size) {
    return {
        gap: size / 20.,
        triangleWidth: size / 100.,
        strokeWidth: size / 300.,
        fontSize: size / 20.,
    };
}
function createFlatPartMeasurements(path, width, height, style) {
    const bbox = boundingBox(path);
    const vertical = measurement({
        from: {
            entries: [
                bbox.upper.entries[0],
                bbox.lower.entries[1],
            ],
        },
        to: bbox.upper,
    }, true, height.toFixed(2) + " mm", style);
    const horizontal = measurement({
        from: bbox.lower,
        to: {
            entries: [
                bbox.upper.entries[0],
                bbox.lower.entries[1],
            ],
        },
    }, true, width.toFixed(2) + " mm", style);
    return [
        ...vertical,
        ...horizontal,
    ];
}
function addFlangeMeasurement(lower, upper, part, beforePart, style) {
    const useLower = part.content.outerAngleInRadians > 0. || (part.content.outerAngleInRadians === 0. && beforePart !== undefined && beforePart.content.outerAngleInRadians > 0.);
    const line = useLower ? lower : upper;
    const vec = vector(line.from, line.to);
    const normalized = normalize(vec);
    const lineLength = Math.sqrt(squareNorm(vec));
    if (Math.abs(lineLength - part.flangeLength.lineLength) > dTol) {
        throw Error(`Wrong line length: ${lineLength} vs. ${part.flangeLength.lineLength}`);
    }
    const length = part.flangeLength.lineLength + part.flangeLength.afterArcLength + part.flangeLength.beforeArcLength;
    if (Math.abs(length - part.content.flangeLength) > dTol) {
        throw Error("Length wrong");
    }
    const fromAddition = -1. * (useLower ? part.flangeLength.beforeArcLength : part.flangeLength.afterArcLength);
    const toAddition = useLower ? part.flangeLength.afterArcLength : part.flangeLength.beforeArcLength;
    return measurement({
        from: add(line.from, mult(fromAddition, normalized)),
        to: add(line.to, mult(toAddition, normalized)),
    }, false, length.toFixed(2) + " mm", style);
}
function addAngleMeasurement(arc, beforeLine, afterLine, lowerCcw, style) {
    const angleOfArc = angle(arc);
    if (Math.abs(Math.abs(angleOfArc) - Math.PI * 1.5) < dTol || Math.abs(Math.abs(angleOfArc) - Math.PI * 0.5) < dTol) {
        return [];
    }
    const additionalWidth = 1.5 * style.gap;
    const arcFrom = add(mult(-additionalWidth, normalize(vector(beforeLine.from, beforeLine.to))), beforeLine.to);
    const arcTo = add(mult(additionalWidth, normalize(vector(afterLine.from, afterLine.to))), afterLine.from);
    const arcCenter = add(mult(0.5, vector(arcFrom, arcTo)), arcFrom);
    const arcSegment = {
        from: arcFrom,
        to: arcTo,
        center: arcCenter,
        ccw: !arc.ccw,
    };
    const path = [
        {
            type: SegmentType.arc,
            content: arcSegment,
        },
    ];
    const elements = [createMeasurementElement(path, style, false, false)];
    {
        let degree = toDegree(angleOfArc);
        if (degree < 0.) {
            degree += +360.;
        }
        degree = 180. - degree;
        const textContent = degree.toFixed(0) + " °";
        const moveVec = {
            entries: [
                0.,
                lowerCcw ? 0. : style.fontSize,
            ],
        };
        elements.push(textElement(add(moveVec, arcCenter), textContent, style));
    }
    return elements;
}
function createProfileMeasurements(path, measurementInfo, style) {
    if (path.length !== (measurementInfo.profileParts.length * 4)) {
        throw Error("Error constructing.");
    }
    const measurements = [];
    const index = path.findIndex(p => p.type === SegmentType.arc);
    const arcSegment = path[index];
    if (arcSegment === undefined) {
        throw Error("Error constructing.");
    }
    if (!isArcSegment(arcSegment.content)) {
        throw Error("Wrong type.");
    }
    const upperIndex = (i) => path.length - 2 - i;
    const partIndex = (i) => i / 2;
    for (let i = 0; i < ((path.length / 2) - 1); ++i) {
        const lower = path[i];
        const upper = path[upperIndex(i)];
        if (lower.type === SegmentType.line) {
            if (upper.type !== SegmentType.line) {
                throw Error("Error constructing.");
            }
            const m = addFlangeMeasurement(lower.content, upper.content, measurementInfo.profileParts[partIndex(i)], i === 0 ? undefined : measurementInfo.profileParts[partIndex(i) - 1], style);
            for (const measure of m) {
                measurements.push(measure);
            }
        }
        else {
            const part = measurementInfo.profileParts[partIndex(i - 1)];
            const useLower = part.content.outerAngleInRadians < 0.;
            const m = addAngleMeasurement(useLower ? lower.content : upper.content, useLower ? path[i - 1].content : path[upperIndex(i + 1)].content, useLower ? path[i + 1].content : path[upperIndex(i - 1)].content, lower.content.ccw, style);
            for (const measure of m) {
                measurements.push(measure);
            }
        }
    }
    return measurements;
}
function createMeasurements(path, width, height, measurementInfo) {
    const size = Math.max(width, height);
    const style = computeMeasurementStyle(size);
    return measurementInfo.profileParts.length === 0 ? createFlatPartMeasurements(path, width, height, style) : createProfileMeasurements(path, measurementInfo, style);
}
function strokeStyle(size) {
    return {
        stroke: "black",
        fill: "#c0c0c0",
        strokeHeight: (size / 300).toString(),
    };
}
function svgElement(viewBox, entries) {
    const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
    svg.setAttribute("viewBox", viewBox);
    for (const e of entries) {
        svg.appendChild(e);
    }
    return svg;
}
function circleSvgElement(path, style) {
    const arc = path[0].content;
    const c = document.createElementNS("http://www.w3.org/2000/svg", "circle");
    c.setAttribute("cx", arc.center.entries[0].toString());
    c.setAttribute("cy", arc.center.entries[1].toString());
    c.setAttribute("r", radius(arc)
        .toString());
    c.setAttribute("fill", style.fill);
    c.setAttribute("stroke", style.stroke);
    c.setAttribute("stroke-width", style.strokeHeight);
    return c;
}
function iopSvgElement(iop, style) {
    if (isCircle(iop.outerPolygon)) {
        if (iop.innerPolygons.length === 0) {
            return circleSvgElement(iop.outerPolygon, style);
        }
        else {
            const p = document.createElementNS("http://www.w3.org/2000/svg", "path");
            p.setAttribute("d", createPathSvgString(iop));
            p.setAttribute("fill", style.fill);
            p.setAttribute("fill-rule", "evenodd");
            p.setAttribute("stroke", style.stroke);
            p.setAttribute("stroke-width", style.strokeHeight);
            return p;
        }
    }
    const p = document.createElementNS("http://www.w3.org/2000/svg", "path");
    p.setAttribute("d", createPathSvgString(iop));
    p.setAttribute("fill", style.fill);
    p.setAttribute("fill-rule", "evenodd");
    p.setAttribute("stroke", style.stroke);
    p.setAttribute("stroke-width", style.strokeHeight);
    return p;
}
export const defaultGeneralInformation = {
    material: {
        cuttingGasId: "",
        invalid: true,
        material: {
            name: "",
            identifier: "",
            description: "",
        },
    },
    articleName: "Default",
    thickness: -1.,
    valid: false,
};
function flipYCoordinate(p, height) {
    return {
        entries: [
            p.entries[0],
            height - p.entries[1],
        ],
    };
}
function flipYCoordinates(path, height) {
    return path.map(s => {
        if (s.type === SegmentType.line) {
            return {
                type: SegmentType.line,
                content: {
                    from: flipYCoordinate(s.content.from, height),
                    to: flipYCoordinate(s.content.to, height),
                },
            };
        }
        else {
            if (!isArcSegment(s.content)) {
                throw Error("Wrong type");
            }
            return {
                type: SegmentType.arc,
                content: {
                    from: flipYCoordinate(s.content.from, height),
                    to: flipYCoordinate(s.content.to, height),
                    center: flipYCoordinate(s.content.center, height),
                    ccw: s.content.ccw,
                },
            };
        }
    });
}
function translateInnerPolygon(iop, xBorder, yBorder, bbox) {
    const tr = (path) => translate(mult(-1., add({
        entries: [
            -xBorder,
            -yBorder,
        ],
    }, bbox.lower)), path);
    return {
        outerPolygon: tr(iop.outerPolygon),
        innerPolygons: iop.innerPolygons.map(i => tr(i)),
    };
}
function flipYCoordinatesInnerOuterPolygon(iop, height) {
    const translatedInnerPolygon = [];
    for (const p of iop.innerPolygons) {
        translatedInnerPolygon.push(flipYCoordinates(p, height));
    }
    const translatedIop = {
        outerPolygon: flipYCoordinates(iop.outerPolygon, height),
        innerPolygons: translatedInnerPolygon,
    };
    return translatedIop;
}
export function createSvg(iop, measurementInfo) {
    const dim = dimensions(iop.outerPolygon);
    const size = Math.max(dim[0], dim[1]);
    const defaultBorder = size / 2.8;
    const fontSize = measurementInfo.addMeasurements ? computeMeasurementStyle(size).fontSize : 0.;
    const gap = measurementInfo.addMeasurements ? computeMeasurementStyle(size).gap : 0.;
    const xBorder = defaultBorder + approximateTextLength(dim[0].toFixed(2) + " mm", fontSize) + gap;
    const yBorder = defaultBorder + approximateTextHeight(fontSize, false) + gap;
    const viewBox = "0 0 " + (dim[0] + 2. * xBorder).toString() + " " + (dim[1] + 2. * yBorder).toString();
    const bbox = boundingBox(iop.outerPolygon);
    const translatedIop = translateInnerPolygon(iop, xBorder, yBorder, bbox);
    const flipped = flipYCoordinatesInnerOuterPolygon(translatedIop, dim[1] + 2. * yBorder);
    const entries = [iopSvgElement(flipped, strokeStyle(size))];
    if (measurementInfo.addMeasurements) {
        const measure = createMeasurements(flipped.outerPolygon, dim[0], dim[1], measurementInfo);
        for (let i = 0; i < measure.length; i++) {
            entries.push(measure[i]);
        }
    }
    let index = 1;
    const style = computeMeasurementStyle(size);
    for (const p of flipped.innerPolygons) {
        if (!isArcSegment(p[0].content)) {
            throw Error("Wrong type.");
        }
        const holeRadius = radius(p[0].content);
        const n = {
            entries: [
                p[0].content.from.entries[0] - (holeRadius + (gap / 4.) + approximateTextLength(index.toString(), fontSize)),
                p[0].content.from.entries[1] - (holeRadius - approximateTextHeight(fontSize, true) / 1.5),
            ],
        };
        const element = textElement(n, index.toString(), style);
        element.setAttribute("stroke", holeLabelColor);
        entries.push(element);
        index++;
    }
    return svgElement(viewBox, entries);
}
export function isOuterPolygonData(t) {
    return isRectanglePolygonData(t) || isCirclePolygonData(t);
}
export function isRectanglePolygonData(t) {
    const typeguardMap = {
        dimX: isNumber,
        dimY: isNumber,
        cornerRadius: isNumber,
        thickness: isNumber,
    };
    return isInstanceOf(t, typeguardMap);
}
export function isCirclePolygonData(t) {
    const typeguardMap = {
        radius: isNumber,
        thickness: isNumber,
    };
    return isInstanceOf(t, typeguardMap);
}
